// The "Is the plan working?" chart — 7-day rolling intake vs
// bodyweight, with training-phase markers and a TDEE reference line.
// Extracted out of SummaryHero.js (which had grown to ~575 lines across 7
// components) — this piece is self-contained (props in, chart out, no
// shared state with the cards below it), so it was the cleanest single cut.
import React, { useState, useMemo, useLayoutEffect, memo } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

import { evenlySpacedTicks, toDisplayWeight, withoutLabelCollisions, CHART_THEME, kcalChartDomain, weightChartDomain } from "./viewTokens";
import { ChartLegend, TooltipShell, tdeeReferenceLineProps } from "./viewAtoms";
import { useCenteredTooltip } from "../../../../../hooks/useCenteredTooltip";

/* ---- constants --------------------------------------------------------- */

const INTAKE_COLOR = "#E8A22D";
const TARGET_COLOR = "var(--white-50)";
const WEIGHT_COLOR = "var(--tracking)"; // #4e81f2
// TARGET_COLOR (var(--white-50)) is theme-aware — flips to a DARK gray in
// light theme, which is correct on the Line (drawn on the theme-flipping
// card) but wrong inside the tooltip: .np-hero__tooltip's background is a
// fixed #1a1e25, never flips with the theme, so a dark-in-light-theme color
// there read as dark-on-dark, nearly invisible. This is the dark-theme
// value of --white-50 hardcoded, since the tooltip itself never changes —
// same fix as ReportsIntakeChart.js's own TARGET_TOOLTIP_COLOR.
const TARGET_TOOLTIP_COLOR = "rgba(255, 255, 255, 0.5)";
// Theme-aware — CHART_THEME (viewTokens.js) used to be 3 hardcoded
// rgba(255,255,255,X) consts here, which read fine on the prototype's
// fixed-dark background but were nearly invisible once the card followed
// the app's real light/dark toggle ([data-theme="light"] switches this card
// to bg-white, see np-hero in nutrition.css) — var(--white-XX) resolves live
// in the SVG (same trick WEIGHT_COLOR already relies on above), so grid/
// ticks/cursor stay legible in both themes. Shared with Reports' own charts.
const { tick: CHART_TICK, tickFontSize: CHART_TICK_SIZE, grid: CHART_GRID } = CHART_THEME;

// Evenly spaced across the full domain (see tickIndices in HeroChart).
const TICK_COUNT = 6;

// One entry per chart line — keeps the 3 near-identical <Line> blocks (and any
// future series) data-driven instead of copy-pasted JSX.
const CHART_LINES = [
  { dataKey: "intake", nameKey: "chart.intake", yAxisId: "kcal", stroke: INTAKE_COLOR, strokeWidth: 2, connectNulls: true },
  { dataKey: "target", nameKey: "chart.target", yAxisId: "kcal", stroke: TARGET_COLOR, strokeWidth: 1.5, strokeDasharray: "4 2", connectNulls: true },
  { dataKey: "weight", nameKey: "chart.bodyweight", yAxisId: "weight", stroke: WEIGHT_COLOR, strokeWidth: 1.5, connectNulls: true, showDot: true },
];

/* ---- i18n helper ------------------------------------------------------- */

const h = (t, key, opts) => t(`nutritionPage.progress.hero.${key}`, opts);

/* ---- custom chart tooltip ---------------------------------------------- */

const ChartTooltip = ({ active, payload, weightUnit, onMeasureWidth }) => (
  <TooltipShell active={active} payload={payload} onMeasureWidth={onMeasureWidth}>
    {(pt, fullPayload) => {
      const fullDate = pt?.utcDate ? moment(String(pt.utcDate), "YYYYMMDD").format("MMM D, YYYY") : pt?.name;
      return (
        <>
          <div className="np-hero__tooltip-date">{fullDate}</div>
          {/* Rows with no data for this day are omitted entirely (not a "—"
              placeholder) — same behavior as ReportsIntakeChart.js's IntakeTooltip. */}
          {fullPayload
            .filter((entry) => entry.value != null)
            .map((entry) => (
              <div
                key={entry.dataKey}
                className="np-hero__tooltip-row"
                style={{ color: entry.dataKey === "target" ? TARGET_TOOLTIP_COLOR : entry.color }}
              >
                <span>{entry.name}:</span>
                <span>
                  {formatTooltipValue(entry.value, entry.dataKey, weightUnit)}
                </span>
              </div>
            ))}
        </>
      );
    }}
  </TooltipShell>
);

const formatTooltipValue = (value, dataKey, weightUnit) => {
  if (dataKey === "weight") return `${Math.round(value * 10) / 10} ${weightUnit}`;
  return `${value.toLocaleString()} kCal`;
};

ChartTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  weightUnit: PropTypes.string,
  onMeasureWidth: PropTypes.func,
};

/* ---- hero chart -------------------------------------------------------- */

export const HeroChart = memo(({ series, phaseAreas, workingExpenditure, decoupled, staleData, weeklyChange, units }) => {
  const { t } = useTranslation("athlete-portal");
  const tdeeLineProps = tdeeReferenceLineProps({ workingExpenditure, decoupled, staleData, weeklyChange, yAxisId: "kcal", t });
  const isImperial = units === "imperial";
  const weightUnit = isImperial ? "lb" : "kg";
  const weightAxisKey = isImperial ? "chart.lbAxis" : "chart.kgAxis";

  // Always routed through toDisplayWeight (not just when isImperial) since it
  // also rounds to 1 decimal even with no unit conversion — skipping it for
  // metric left the raw, unrounded kg weigh-in in the tooltip (same fix as
  // ReportsIntakeChart.js's own displaySeries).
  const displaySeries = useMemo(
    () => series.map((pt) => ({ ...pt, weight: toDisplayWeight(pt.weight, isImperial) })),
    [series, isImperial]
  );

  // Evenly spaced across the FULL domain, always including the last day —
  // see evenlySpacedTicks (viewTokens.js) for why interpolated, not stepped.
  const tickIndices = useMemo(
    () => evenlySpacedTicks(displaySeries.length - 1, TICK_COUNT),
    [displaySeries]
  );

  // Zoom the kcal axis to the data (not Recharts' 0-anchored default) while
  // guaranteeing every drawn line fits inside it — see kcalChartDomain
  // (viewTokens.js) for why the trim can't be allowed to clip a plotted line.
  const kcalDomain = useMemo(
    () => kcalChartDomain(displaySeries, workingExpenditure),
    [displaySeries, workingExpenditure]
  );

  // Same zoom for the weight axis — it defaulted to Recharts' 0-anchored
  // domain too, showing "0/20/40/60/80" for a client whose real weight never
  // leaves the 70s. minSpan overrides the 200 default (sized for kcal) down
  // to a few units — a body-weight axis realistically never needs a 200
  // kg/lb-wide floor the way a kcal one can.
  const weightDomain = useMemo(
    () => weightChartDomain(displaySeries.map((pt) => pt.weight)),
    [displaySeries]
  );
  // Fallback when computeAxisDomain bails (a single bad weigh-in in the
  // range makes the raw span balloon past the trimmed one) — NOT Recharts'
  // raw 0-anchored default. That default is a reasonable fallback for kcal
  // (already hundreds/thousands, so "wasted space" is mild) but is a real
  // regression for weight: a single bad point can push the axis all the way
  // to 0-80 for a client whose real weight sits at 73-74, exactly the
  // "wasted space" bug this whole feature exists to fix. A plain min/max ±1
  // still surfaces the bad point (doesn't hide it) without going that far —
  // same reasoning as BodyweightGoal.js's own fallback.
  const [naiveWeightMin, naiveWeightMax] = useMemo(() => {
    const vals = displaySeries.map((pt) => pt.weight).filter((v) => v != null && Number.isFinite(v));
    if (!vals.length) return [0, 1];
    return [Math.floor(Math.min(...vals) - 1), Math.ceil(Math.max(...vals) + 1)];
  }, [displaySeries]);

  // No weigh-ins at all anywhere in the range means naiveWeightMin/Max above
  // is the meaningless [0, 1] placeholder — showing an axis (and legend
  // entry) with a "Kg"/"Lb" label and 0/1 ticks over a line with zero points
  // is a fake scale, not a real empty state. Suppress the whole weight axis
  // (+ its Line + its legend entry) rather than render that.
  const hasWeightData = useMemo(
    () => displaySeries.some((pt) => pt.weight != null),
    [displaySeries]
  );

  // Legend labels are intentionally shorter than the <Line>s' own `name`
  // (still "Intake (7d avg)"/"Target (7d avg)", used by the tooltip via
  // CHART_LINES' nameKey) — same split ReportsIntakeChart.js already uses.
  const legendPayload = useMemo(
    () => [
      { value: h(t, "chart.legendIntake"), color: INTAKE_COLOR, dataKey: "intake" },
      { value: h(t, "chart.legendTarget"), color: TARGET_COLOR, dataKey: "target" },
      ...(hasWeightData ? [{ value: h(t, "chart.bodyweight"), color: WEIGHT_COLOR, dataKey: "weight" }] : []),
    ],
    [t, hasWeightData]
  );

  // Manual tooltip centering — see useCenteredTooltip's own header comment
  // for the full "why" (Recharts doesn't do this by default). Same fix
  // already applied to ReportsIntakeChart.js.
  const { containerRef, tooltipPosition, onMeasureWidth, chartHandlers } = useCenteredTooltip({
    initialWidth: 180,
  });

  // Real container width, measured once after mount (reusing the same
  // containerRef the tooltip centering above already attaches) — needed to
  // turn each phase marker's day-index into an actual pixel position for
  // withoutLabelCollisions below. 0 on the very first render, before the
  // container has mounted; that's fine, withoutLabelCollisions falls back to
  // showing every label until this measurement lands.
  const [containerWidth, setContainerWidth] = useState(0);
  useLayoutEffect(() => {
    setContainerWidth(containerRef.current?.offsetWidth ?? 0);
  }, [containerRef]);

  // Phase transition markers (training + nutrition) — see the <ReferenceLine>
  // block below for the full "why" of the line-vs-band choice. Long ranges can
  // pack many real, distinct transitions into the same pixel width (a coach
  // changing phase every week over years of history) — showLabel keeps the
  // LINE for every one of them (still marks "something changed here") but
  // drops the TEXT for whichever ones land too close to the last label
  // actually shown, so dense stretches don't render as overlapping,
  // unreadable text. A phase that was already running before the visible
  // window opened (clipped to x1=0 in buildPhaseSpans) goes through this
  // exact same rule rather than being dropped outright — it still tells you
  // what phase you're in even without a real in-view transition, as long as
  // nothing else is competing for that same left edge.
  // Collision-avoidance runs PER TRACK, not on the combined list - training
  // and nutrition are two independent timelines, and at any given x (the
  // window's left edge especially, where an isOngoing phase from each track
  // routinely lands on the exact same day) a coach wants both, not whichever
  // one happened to sort first. A single shared sweep would let training
  // permanently win that slot and starve nutrition's label every time.
  const labeledPhaseAreas = useMemo(() => {
    const collisionOptions = {
      xKey: "x1",
      containerWidth,
      totalDays: displaySeries.length - 1,
    };
    const withLabels = (track) =>
      withoutLabelCollisions(
        phaseAreas
          .filter((area) => area.track === track)
          .sort((a, b) => a.x1 - b.x1),
        collisionOptions
      );
    return [...withLabels("training"), ...withLabels("nutrition")];
  }, [phaseAreas, containerWidth, displaySeries.length]);

  return (
    <div ref={containerRef}>
      <ResponsiveContainer width="99%" height={280}>
        <ComposedChart
          data={displaySeries}
          margin={{ top: 36, right: 52, bottom: 0, left: 0 }}
          {...chartHandlers}
        >
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <XAxis
            dataKey="idx"
            type="number"
            domain={[0, Math.max(displaySeries.length - 1, 1)]}
            ticks={tickIndices}
            tickFormatter={(i) => displaySeries[i]?.name ?? ""}
            tick={{ fontSize: CHART_TICK_SIZE, fill: CHART_TICK }}
            tickLine={false}
            axisLine={false}
            padding={{ left: 32, right: 32 }}
          />
          <YAxis
            yAxisId="kcal"
            domain={kcalDomain ?? undefined}
            allowDataOverflow={kcalDomain != null}
            tick={{ fontSize: CHART_TICK_SIZE, fill: CHART_TICK }}
            tickLine={false}
            axisLine={false}
            width={44}
            label={{ value: h(t, "chart.kcalAxis"), position: "top", offset: 16, fontSize: 11, fontWeight: 700, fill: "var(--primary)" }}
          />
          {hasWeightData && (
            <YAxis
              yAxisId="weight"
              orientation="right"
              domain={weightDomain ?? [naiveWeightMin, naiveWeightMax]}
              allowDataOverflow={weightDomain != null}
              tick={{ fontSize: CHART_TICK_SIZE, fill: CHART_TICK }}
              tickLine={false}
              axisLine={false}
              width={36}
              label={{ value: h(t, weightAxisKey), position: "top", offset: 16, fontSize: 11, fontWeight: 700, fill: "var(--primary)" }}
            />
          )}
          <Tooltip
            content={<ChartTooltip weightUnit={weightUnit} onMeasureWidth={onMeasureWidth} />}
            cursor={{ stroke: "var(--primary)", strokeWidth: 1 }}
            position={tooltipPosition}
          />
          <Legend
            content={<ChartLegend coloredText />}
            payload={legendPayload}
            wrapperStyle={{ paddingTop: 8 }}
          />

          {/* Phase transition markers (both training and nutrition tracks) —
              a vertical line + label at the point a phase STARTS, not a shaded
              band across the whole span. The prototype reserved the band
              treatment for diet/nutrition phases; for now both tracks render
              the same line marker. A span whose clipped x1 is 0 means it was
              already running before the visible range opened (or coincides
              with the range's first day) — there's no real in-view transition
              there, but the line still renders (it tells you what phase
              you're already in); only its label is subject to the same
              per-track collision check as everything else (see
              labeledPhaseAreas above), so it yields to a genuine nearby
              transition ON THE SAME TRACK instead of always winning just for
              being first. isOngoing (only true for the "already running" case
              - see buildPhaseSpans, dateRange.js) fades both the line and the
              label so it still reads as distinct from a real in-view
              transition, rather than implying the phase started exactly at
              the window's edge. Every marker keeps its line regardless; only
              the label is dropped when showLabel is false (too close to the
              last label actually shown, within that same track). Training
              and nutrition can each have a marker on the exact same day (a
              training phase starting the same week a nutrition phase does,
              or - now common - both simply being isOngoing at the window's
              left edge) - label `offset` is staggered per track (training
              closer to the line, nutrition further above) so the two texts
              stack instead of literally overlapping at that shared x; the
              key needs the track too since x1 alone isn't unique anymore. */}
          {labeledPhaseAreas.map((area) => (
            <ReferenceLine
              key={`${area.track}-${area.x1}`}
              yAxisId="kcal"
              x={area.x1}
              stroke={area.color}
              strokeOpacity={area.isOngoing ? 0.5 : 1}
              strokeDasharray={area.isOngoing ? "2 3" : "4 2"}
              label={
                area.showLabel
                  ? {
                      value: area.name,
                      position: "top",
                      offset: area.track === "training" ? 5 : 20,
                      fontSize: 11,
                      fontWeight: 600,
                      fill: area.color,
                      opacity: area.isOngoing ? 0.6 : 1,
                    }
                  : undefined
              }
            />
          ))}

          {CHART_LINES.filter((line) => hasWeightData || line.dataKey !== "weight").map((line) => (
            <Line
              key={line.dataKey}
              name={h(t, line.nameKey)}
              yAxisId={line.yAxisId}
              dataKey={line.dataKey}
              stroke={line.stroke}
              strokeWidth={line.strokeWidth}
              strokeDasharray={line.strokeDasharray}
              dot={line.showDot ? { r: 2.5, fill: line.stroke, strokeWidth: 0 } : false}
              activeDot={{ r: 5, strokeWidth: 0 }}
              connectNulls={line.connectNulls}
              type="monotone"
              isAnimationActive={false}
            />
          ))}

          {/* TDEE reference line — declared last (not right after the phase
              areas) so it paints on top of the Line series above — SVG has no
              z-index, later-declared siblings paint over earlier ones, and the
              decoupled tooltip's foreignObject was getting drawn under the
              lines when this was declared first. */}
          {tdeeLineProps && <ReferenceLine {...tdeeLineProps} />}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
});

HeroChart.propTypes = {
  series: PropTypes.array.isRequired,
  phaseAreas: PropTypes.array.isRequired,
  workingExpenditure: PropTypes.number,
  decoupled: PropTypes.bool,
  staleData: PropTypes.bool,
  weeklyChange: PropTypes.number,
  units: PropTypes.string,
};
